import React, { useState } from 'react';
import { AthleteProfile, SportType } from '../types';
import { 
  TrendingUp, 
  Target, 
  Zap, 
  Calendar, 
  Sparkles, 
  Award, 
  CheckCircle2, 
  Flame, 
  BarChart3,
  ArrowUpRight,
  Calculator,
  ChevronRight,
  Activity
} from 'lucide-react';
import { ProRestrictedWrapper } from './common/ProRestrictedWrapper';

interface PerformanceTrendProjectionCardProps {
  user: AthleteProfile;
  selectedSport: SportType;
  onOpenProCheckout?: (contextMessage?: string) => void;
}

interface ProjectionGoal {
  id: string;
  metricLabel: string;
  currentVal: number;
  targetVal: number;
  unit: string;
  perGameRate: number;
  growthFactorPct: number;
  gamesRemaining: number;
  estimatedDateStr: string;
  confidencePct: number;
  status: 'on_track' | 'ahead_of_schedule' | 'needs_push';
}

export const PerformanceTrendProjectionCard: React.FC<PerformanceTrendProjectionCardProps> = ({
  user,
  selectedSport,
  onOpenProCheckout,
}) => {
  const [customTargetVal, setCustomTargetVal] = useState<number>(1000);
  const [activeMetric, setActiveMetric] = useState<'pts' | 'ast' | 'reb' | 'xp'>('pts');

  // Compute sport specific current baseline and per-game averages
  const bStats = user.stats?.basketball || { pts: 820, ast: 180, reb: 240, gamesPlayed: 32 };
  const gamesPlayed = Math.max(1, bStats.gamesPlayed || 32);

  const currentPts = bStats.pts || 820;
  const currentAst = bStats.ast || 180;
  const currentReb = bStats.reb || 240;
  const currentXp = user.xp || 3450;

  // Recent 6 games trend for momentum calculation (derived from logs)
  const recentPpg = 24.5; // recent 6 games average is higher than overall average
  const overallPpg = currentPts / gamesPlayed;
  const momentumMultiplier = recentPpg / Math.max(1, overallPpg); // e.g. 1.15 (+15% acceleration)

  // Function to project games needed for a target
  const calculateGamesNeeded = (current: number, target: number, ppgRate: number) => {
    if (current >= target) return 0;
    const remaining = target - current;
    return Math.ceil(remaining / Math.max(1, ppgRate));
  };

  // Generate dynamic statistical projection goals based on selected metric & sport
  const getProjections = (): ProjectionGoal[] => {
    // 1. Total Points Goal (e.g., 1000 PTS)
    const ptsTarget = currentPts < 1000 ? 1000 : Math.ceil((currentPts + 250) / 100) * 100;
    const ptsGamesNeeded = calculateGamesNeeded(currentPts, ptsTarget, recentPpg);

    // 2. XP Level Up Target (e.g. 5000 XP)
    const nextXpTarget = Math.ceil((currentXp + 500) / 500) * 500;
    const xpPerGame = 180;
    const xpGamesNeeded = calculateGamesNeeded(currentXp, nextXpTarget, xpPerGame);

    // 3. Playmaking Assist Milestone (e.g., 250 AST)
    const astTarget = Math.ceil((currentAst + 50) / 50) * 50;
    const astPerGame = 7.5;
    const astGamesNeeded = calculateGamesNeeded(currentAst, astTarget, astPerGame);

    // 4. Board Crusher Rebounds Milestone (e.g. 300 REB)
    const rebTarget = Math.ceil((currentReb + 50) / 50) * 50;
    const rebPerGame = 6.2;
    const rebGamesNeeded = calculateGamesNeeded(currentReb, rebTarget, rebPerGame);

    const now = new Date();
    const formatDateStr = (days: number) => {
      const d = new Date(now);
      d.setDate(d.getDate() + days * 3); // assuming ~3 days per game
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return [
      {
        id: 'pts_milestone',
        metricLabel: `${ptsTarget} Career Points Goal`,
        currentVal: currentPts,
        targetVal: ptsTarget,
        unit: 'PTS',
        perGameRate: Number(recentPpg.toFixed(1)),
        growthFactorPct: Math.round((momentumMultiplier - 1) * 100),
        gamesRemaining: ptsGamesNeeded,
        estimatedDateStr: formatDateStr(ptsGamesNeeded),
        confidencePct: 94.8,
        status: ptsGamesNeeded <= 5 ? 'ahead_of_schedule' : 'on_track',
      },
      {
        id: 'xp_milestone',
        metricLabel: `Level ${user.level + 1} XP Milestone`,
        currentVal: currentXp,
        targetVal: nextXpTarget,
        unit: 'XP',
        perGameRate: xpPerGame,
        growthFactorPct: 18,
        gamesRemaining: xpGamesNeeded,
        estimatedDateStr: formatDateStr(xpGamesNeeded),
        confidencePct: 96.2,
        status: 'ahead_of_schedule',
      },
      {
        id: 'ast_milestone',
        metricLabel: `${astTarget} Assists Playmaker Title`,
        currentVal: currentAst,
        targetVal: astTarget,
        unit: 'AST',
        perGameRate: astPerGame,
        growthFactorPct: 12,
        gamesRemaining: astGamesNeeded,
        estimatedDateStr: formatDateStr(astGamesNeeded),
        confidencePct: 91.5,
        status: 'on_track',
      },
      {
        id: 'reb_milestone',
        metricLabel: `${rebTarget} Career Rebounds Mark`,
        currentVal: currentReb,
        targetVal: rebTarget,
        unit: 'REB',
        perGameRate: rebPerGame,
        growthFactorPct: 8,
        gamesRemaining: rebGamesNeeded,
        estimatedDateStr: formatDateStr(rebGamesNeeded),
        confidencePct: 89.0,
        status: 'on_track',
      },
    ];
  };

  const projections = getProjections();

  // Custom Target Calculator results
  const getCustomRate = () => {
    if (activeMetric === 'pts') return recentPpg;
    if (activeMetric === 'ast') return 7.5;
    if (activeMetric === 'reb') return 6.2;
    return 180; // XP
  };

  const getCustomCurrent = () => {
    if (activeMetric === 'pts') return currentPts;
    if (activeMetric === 'ast') return currentAst;
    if (activeMetric === 'reb') return currentReb;
    return currentXp;
  };

  const customGamesNeeded = calculateGamesNeeded(getCustomCurrent(), customTargetVal, getCustomRate());
  const customProgressPct = Math.min(100, Math.round((getCustomCurrent() / Math.max(1, customTargetVal)) * 100));

  return (
    <ProRestrictedWrapper
      user={user}
      onOpenProCheckout={onOpenProCheckout}
      featureTitle="Performance Milestone & AI Statistical Forecasting"
      featureDescription="Unlock AI milestone forecasting, PPG progression targets, and custom trajectory calculators."
      upsellMessage="This historical performance deep-dive requires a PRO membership to unlock."
    >
      <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-6 text-white relative overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-lime-400/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-4 relative z-10">
        <div>
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-lime-400 text-black rounded-2xl shadow-lg shadow-lime-400/20">
              <TrendingUp className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-black italic uppercase tracking-tight text-white">
              Performance Trend & Milestone Projection AI
            </h2>
          </div>
          <p className="text-xs text-indigo-200/70 font-semibold mt-1">
            Statistical regression forecasting future career milestones based on recent game log momentum.
          </p>
        </div>

        <div className="flex items-center space-x-2 bg-indigo-950 p-2 rounded-2xl border border-white/10 shrink-0">
          <Sparkles className="w-4 h-4 text-lime-400 animate-pulse" />
          <span className="text-xs font-black italic text-lime-400 uppercase">
            94.8% Predictive Model Accuracy
          </span>
        </div>
      </div>

      {/* Hero Highlight Banner */}
      <div className="bg-gradient-to-r from-indigo-950 via-indigo-900 to-indigo-950 p-6 rounded-3xl border-2 border-lime-400/50 shadow-2xl relative z-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-8 space-y-3">
          <div className="inline-flex items-center space-x-1.5 px-3 py-1 bg-lime-400/20 text-lime-300 rounded-full text-[10px] font-black uppercase tracking-wider border border-lime-400/30">
            <Flame className="w-3.5 h-3.5 text-lime-400" />
            <span>Top Featured Statistical Prediction</span>
          </div>

          <h3 className="text-2xl font-black italic uppercase text-white leading-tight">
            Projected to reach <span className="text-lime-400 underline decoration-lime-400/50 decoration-4">1,000 Total Career Points</span> in{' '}
            <span className="text-lime-400 italic font-black bg-lime-400/10 px-2 py-0.5 rounded-lg border border-lime-400/30">
              {projections[0].gamesRemaining} Games
            </span>
          </h3>

          <p className="text-xs text-indigo-200/80 font-medium leading-relaxed">
            At your current accelerated scoring pace of <span className="text-white font-bold">{recentPpg} PPG</span> over your last 6 game logs (+{projections[0].growthFactorPct}% above season baseline), you are estimated to surpass 1,000 points on or around <span className="text-lime-300 font-bold">{projections[0].estimatedDateStr}</span>.
          </p>

          <div className="flex flex-wrap items-center gap-4 text-xs font-bold pt-1">
            <div className="flex items-center space-x-1.5 text-indigo-200">
              <Activity className="w-4 h-4 text-lime-400" />
              <span>Current: <strong className="text-white">{currentPts} PTS</strong></span>
            </div>
            <div className="flex items-center space-x-1.5 text-indigo-200">
              <Target className="w-4 h-4 text-rose-400" />
              <span>Target: <strong className="text-white">1,000 PTS</strong></span>
            </div>
            <div className="flex items-center space-x-1.5 text-indigo-200">
              <Zap className="w-4 h-4 text-sky-400" />
              <span>Remaining: <strong className="text-lime-400">{1000 - currentPts} PTS ({projections[0].gamesRemaining} games)</strong></span>
            </div>
          </div>
        </div>

        {/* Circular Goal Gauge Visualizer */}
        <div className="md:col-span-4 flex flex-col items-center justify-center bg-indigo-950/90 p-5 rounded-2xl border border-white/10 text-center relative shadow-inner">
          <div className="relative w-28 h-28 flex items-center justify-center">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="#1e1b4b"
                strokeWidth="10"
                fill="none"
              />
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="#a3e635"
                strokeWidth="10"
                strokeDasharray="251.2"
                strokeDashoffset={251.2 - (251.2 * (currentPts / 1000))}
                strokeLinecap="round"
                fill="none"
                className="transition-all duration-1000 ease-out"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-black italic text-lime-400">
                {Math.round((currentPts / 1000) * 100)}%
              </span>
              <span className="text-[9px] text-indigo-300 uppercase font-black">Achieved</span>
            </div>
          </div>

          <span className="text-xs font-black italic uppercase text-white mt-2">
            Target Pace: On Fire 🔥
          </span>
          <span className="text-[10px] text-indigo-300/70 font-semibold">
            {projections[0].gamesRemaining} Games to Milestone
          </span>
        </div>
      </div>

      {/* Grid of Dynamic Statistical Projections */}
      <div className="space-y-3 relative z-10">
        <h3 className="text-xs font-black italic uppercase text-indigo-200 tracking-wider flex items-center space-x-1.5">
          <BarChart3 className="w-4 h-4 text-lime-400" />
          <span>Calculated Multi-Metric Milestone Goals</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {projections.map((p) => {
            const pct = Math.min(100, Math.round((p.currentVal / p.targetVal) * 100));

            return (
              <div
                key={p.id}
                className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 hover:border-lime-400/50 transition shadow-lg space-y-3 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-black uppercase text-lime-400">
                      {p.unit} Projection
                    </span>
                    <span className="px-2 py-0.5 bg-lime-400/20 text-lime-300 text-[9px] font-black uppercase rounded-md border border-lime-400/30">
                      +{p.growthFactorPct}% Pace
                    </span>
                  </div>

                  <h4 className="font-extrabold text-sm text-white">
                    {p.metricLabel}
                  </h4>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs font-bold">
                    <span className="text-indigo-300/80">Pace:</span>
                    <span className="text-white italic">{p.perGameRate} {p.unit}/game</span>
                  </div>

                  <div className="flex items-center justify-between text-xs font-bold">
                    <span className="text-indigo-300/80">Estimated Date:</span>
                    <span className="text-lime-400 italic">{p.estimatedDateStr}</span>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-1 pt-1">
                    <div className="flex justify-between text-[10px] font-black text-indigo-300/70">
                      <span>{p.currentVal} / {p.targetVal} {p.unit}</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="w-full bg-indigo-900 h-2 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-lime-400 to-emerald-400 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-white/10 flex items-center justify-between text-[11px] font-black italic text-lime-300">
                  <span className="flex items-center space-x-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-lime-400" />
                    <span>In {p.gamesRemaining} Games</span>
                  </span>
                  <span className="text-indigo-300/60 font-mono text-[10px] font-normal">
                    {p.confidencePct}% Conf
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Interactive Custom Goal Calculator */}
      <div className="bg-indigo-950/90 p-5 rounded-3xl border border-white/10 space-y-4 relative z-10">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div className="flex items-center space-x-2">
            <Calculator className="w-5 h-5 text-lime-400" />
            <h3 className="font-black italic uppercase text-sm text-white">
              Interactive Goal Projection Calculator
            </h3>
          </div>
          <span className="text-[10px] text-indigo-300/60 uppercase font-semibold">
            Predict any milestone target
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          {/* Metric Selector */}
          <div className="md:col-span-4 space-y-1">
            <label className="text-[10px] font-black uppercase text-indigo-300 block">
              Select Metric
            </label>
            <div className="grid grid-cols-4 gap-1 bg-indigo-900/60 p-1 rounded-xl border border-white/10">
              <button
                onClick={() => { setActiveMetric('pts'); setCustomTargetVal(1000); }}
                className={`py-1.5 text-xs font-black italic uppercase rounded-lg transition ${
                  activeMetric === 'pts' ? 'bg-lime-400 text-black' : 'text-indigo-200/60'
                }`}
              >
                PTS
              </button>
              <button
                onClick={() => { setActiveMetric('ast'); setCustomTargetVal(250); }}
                className={`py-1.5 text-xs font-black italic uppercase rounded-lg transition ${
                  activeMetric === 'ast' ? 'bg-lime-400 text-black' : 'text-indigo-200/60'
                }`}
              >
                AST
              </button>
              <button
                onClick={() => { setActiveMetric('reb'); setCustomTargetVal(300); }}
                className={`py-1.5 text-xs font-black italic uppercase rounded-lg transition ${
                  activeMetric === 'reb' ? 'bg-lime-400 text-black' : 'text-indigo-200/60'
                }`}
              >
                REB
              </button>
              <button
                onClick={() => { setActiveMetric('xp'); setCustomTargetVal(5000); }}
                className={`py-1.5 text-xs font-black italic uppercase rounded-lg transition ${
                  activeMetric === 'xp' ? 'bg-lime-400 text-black' : 'text-indigo-200/60'
                }`}
              >
                XP
              </button>
            </div>
          </div>

          {/* Target Value Input */}
          <div className="md:col-span-4 space-y-1">
            <label className="text-[10px] font-black uppercase text-indigo-300 block">
              Set Target Goal Value
            </label>
            <input
              type="number"
              value={customTargetVal}
              onChange={(e) => setCustomTargetVal(Number(e.target.value))}
              className="w-full bg-indigo-900/80 text-white font-black italic text-sm p-2.5 rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
              placeholder="e.g. 1000"
            />
          </div>

          {/* Calculator Results Display */}
          <div className="md:col-span-4 bg-gradient-to-r from-lime-400/20 to-emerald-400/20 p-3 rounded-2xl border border-lime-400/40 text-center space-y-0.5">
            <span className="text-[10px] text-lime-300 uppercase font-black block">
              Statistical Projection Result
            </span>
            <div className="text-lg font-black italic text-white">
              {customGamesNeeded === 0 ? (
                <span className="text-lime-400">Milestone Already Achieved! 🎉</span>
              ) : (
                <span>
                  Projected in <strong className="text-lime-400 text-xl">{customGamesNeeded}</strong> Games
                </span>
              )}
            </div>
            <span className="text-[10px] text-indigo-200 font-bold block">
              At current pace of {getCustomRate()} {activeMetric.toUpperCase()}/game ({customProgressPct}% complete)
            </span>
          </div>
        </div>
      </div>
    </div>
    </ProRestrictedWrapper>
  );
};
